// ABOUTME: Refactored SharePoint File Browser Modal using extracted components and hooks
// ABOUTME: Provides file browsing interface with search, navigation, and infinite scroll

import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    CloseIcon,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@ds/coolshop';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ISharePointItem } from './api/types';
import { isSharePointFolder } from './api/types';
import { BreadcrumbNavigation } from './components/BreadcrumbNavigation';
import { FileListItem } from './components/FileListItem';
import { InfiniteScrollBottomUI } from './components/InfiniteScrollBottomUI';
import { SearchControls } from './components/SearchControls';
import { SelectionSummary } from './components/SelectionSummary';
import { useBulkSelection } from './hooks/useBulkSelection';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { useSharePointApi } from './hooks/useSharePointApi';

type ISharePointFileBrowserModalProps = {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly onSelect: (files: ISharePointItem[]) => void;
    readonly hierarchyId: string;
    readonly initialFile?: ISharePointItem | null;
    readonly isBulkMode?: boolean;
};

export const SharePointFileBrowserModal: React.FC<ISharePointFileBrowserModalProps> = ({
    open,
    onClose,
    onSelect,
    hierarchyId,
    initialFile,
    isBulkMode = false,
}) => {
    const intl = useIntl();

    // Local state
    const [searchTerm, setSearchTerm] = useState('');
    const [pageSize, setPageSize] = useState(25);

    // Refs
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // SharePoint API hook
    const {
        files,
        searchResults,
        breadcrumbs,
        driveItemId,
        totalCount,
        loading,
        loadingMore,
        loadingMoreSearch,
        error,
        nextLink,
        hasMore,
        searchNextLink,
        searchHasMore,
        browserItems,
        searchItems: searchItemsApi,
        fetchItems,
        generateNodes,
        handleFolderOpen: handleFolderOpenApi,
        handleBreadcrumbClick: handleBreadcrumbClickApi,
        browseInfiniteScrollState,
        searchInfiniteScrollState,
        reset,
    } = useSharePointApi({
        hierarchyId,
        pageSize,
    });

    // Bulk selection hook
    const {
        selectedItems,
        isItemSelected,
        isIndeterminate,
        handleSelectionChange,
        handleRemoveFromSummary,
        clearSelection,
    } = useBulkSelection();

    // Computed values
    const filteredFiles = searchTerm ? searchResults : files;
    const isSearchMode = Boolean(searchTerm);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            reset();
            setSearchTerm('');
            setPageSize(25);
            clearSelection();
        }
    }, [open, reset, clearSelection]);

    // Load initial data when modal opens
    useEffect(() => {
        if (open && hierarchyId) {
            browserItems(!initialFile ? '' : initialFile.parentReference.id);
        }
    }, [open, hierarchyId, initialFile]);

    // Infinite scroll callback for browse mode
    const handleInfiniteScrollLoadMore = useCallback(() => {
        if (!searchTerm && nextLink && hasMore && !loadingMore && driveItemId) {
            browseInfiniteScrollState.startLoading();
            browserItems(driveItemId, true, nextLink);
        }
    }, [
        searchTerm,
        nextLink,
        hasMore,
        loadingMore,
        browseInfiniteScrollState,
        browserItems,
        driveItemId,
    ]);

    // Infinite scroll callback for search mode
    const handleInfiniteScrollLoadMoreSearch = useCallback(() => {
        if (searchTerm && searchNextLink && searchHasMore && !loadingMoreSearch) {
            searchInfiniteScrollState.startLoading();
            searchItemsApi(searchTerm.trim(), true, searchNextLink);
        }
    }, [
        searchTerm,
        searchNextLink,
        searchHasMore,
        loadingMoreSearch,
        searchInfiniteScrollState,
        searchItemsApi,
    ]);

    // Setup infinite scroll for browse mode
    useInfiniteScroll({
        containerRef: scrollContainerRef,
        onLoadMore: handleInfiniteScrollLoadMore,
        hasMore: !searchTerm && hasMore && browseInfiniteScrollState.canLoadMore,
        isLoading: browseInfiniteScrollState.isLoading,
        threshold: 0.7,
        debounceMs: 100,
    });

    // Setup infinite scroll for search mode
    useInfiniteScroll({
        containerRef: scrollContainerRef,
        onLoadMore: handleInfiniteScrollLoadMoreSearch,
        hasMore: Boolean(searchTerm) && searchHasMore && searchInfiniteScrollState.canLoadMore,
        isLoading: searchInfiniteScrollState.isLoading,
        threshold: 0.7,
        debounceMs: 100,
    });

    // Handle folder navigation
    const handleFolderOpen = useCallback(
        (folder: ISharePointItem) => {
            // Clear search when navigating to new folder
            setSearchTerm('');
            handleFolderOpenApi(folder);
        },
        [handleFolderOpenApi, selectedItems.length],
    );

    // Handle breadcrumb navigation
    const handleBreadcrumbClick = useCallback(
        (index: number) => {
            // Clear search when navigating via breadcrumbs
            setSearchTerm('');
            handleBreadcrumbClickApi(index);
        },
        [handleBreadcrumbClickApi, selectedItems.length],
    );

    // Handle file/folder selection
    const handleItemsSelect = useCallback(
        async (items: ISharePointItem[]) => {
            try {
                const itemCtagsIds = items.map(item => item.cTagParsed);
                const detailedItems = await fetchItems(itemCtagsIds);

                // NEW: Only in bulk mode, call generateNodes
                if (isBulkMode) {
                    const genRes = await generateNodes(hierarchyId, detailedItems);
                    const snackType = genRes.errors ? 'warning' : 'success';
                    SnackBarUtils[snackType](
                        `Created: ${genRes.nodeCreated || 0}, Updated: ${
                            genRes.nodeContentUpdated || 0
                        }, Errors: ${genRes.errors || 0}`,
                    );
                }

                onSelect(detailedItems); // Always pass original items
                onClose();
            } catch (fetchError) {
                // Error is already handled by the hook
            }
        },
        [isBulkMode, fetchItems, generateNodes, hierarchyId, onSelect, onClose],
    );

    // Handle item clicks (files vs folders)
    const handleItemClick = useCallback(
        (item: ISharePointItem) => {
            if (isSharePointFolder(item)) {
                handleFolderOpen(item);
            } else {
                handleItemsSelect([item]);
            }
        },
        [handleFolderOpen, handleItemsSelect],
    );

    // Handle load more for browse mode
    const handleLoadMore = useCallback(() => {
        if (nextLink && hasMore && !loadingMore && driveItemId) {
            browseInfiniteScrollState.reset();
            browserItems(driveItemId, true, nextLink);
        }
    }, [nextLink, hasMore, loadingMore, browseInfiniteScrollState, browserItems, driveItemId]);

    // Handle load more for search mode
    const handleLoadMoreSearch = useCallback(() => {
        if (searchNextLink && searchHasMore && !loadingMoreSearch && searchTerm.trim()) {
            searchInfiniteScrollState.reset();
            searchItemsApi(searchTerm.trim(), true, searchNextLink);
        }
    }, [
        searchNextLink,
        searchHasMore,
        loadingMoreSearch,
        searchTerm,
        searchInfiniteScrollState,
        searchItemsApi,
    ]);

    // Handle page size change
    const handlePageSizeChange = useCallback(
        (newPageSize: number) => {
            setPageSize(newPageSize);

            if (searchTerm.trim()) {
                // If in search mode, restart search with new page size
                searchItemsApi(searchTerm.trim(), false, undefined, newPageSize);
            } else if (driveItemId) {
                // If in browse mode, restart browsing with new page size
                browserItems(driveItemId, false, undefined, newPageSize);
            }
        },
        [browserItems, driveItemId, searchTerm, searchItemsApi],
    );

    // Debounced search
    useEffect(() => {
        if (!searchTerm.trim()) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            searchItemsApi(searchTerm.trim());
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, searchItemsApi]);

    // Handle search input change
    const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    }, []);

    // Memoize file list items for performance
    const fileListItems = useMemo(
        () =>
            filteredFiles.map(item => {
                const showCheckbox = isBulkMode;

                return (
                    <FileListItem
                        key={`${item.driveItemId}-${item.cTagParsed}-${item.name}`}
                        item={item}
                        isSearchMode={isSearchMode}
                        onItemClick={handleItemClick}
                        isBulkMode={isBulkMode}
                        isSelected={isItemSelected(item)}
                        hasChildrenSelected={isIndeterminate(item)}
                        onSelectionChange={handleSelectionChange}
                        showCheckbox={showCheckbox}
                    />
                );
            }),
        [
            filteredFiles,
            isBulkMode,
            isSearchMode,
            handleItemClick,
            isItemSelected,
            isIndeterminate,
            handleSelectionChange,
        ],
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth disablePaddings>
            <Box height="100vh">
                <DialogTitle>
                    <Box display="flex">
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                        <Box textAlign="center" width={1}>
                            {isBulkMode
                                ? intl.formatMessage({
                                      id: 'sharepoint.bulk-file-selection',
                                      defaultMessage: 'Selezione File in Blocco',
                                  })
                                : intl.formatMessage({
                                      id: 'sharepoint.sharepoint-media',
                                      defaultMessage: 'Media SharePoint',
                                  })}
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {loading && (
                        <Backdrop open={loading} zIndex={1301}>
                            <CircularProgress />
                        </Backdrop>
                    )}

                    {/* Selected Items Summary - always shown when in bulk mode */}
                    {isBulkMode && (
                        <>
                            <SelectionSummary
                                selectedItems={selectedItems}
                                onRemoveItem={handleRemoveFromSummary}
                            />

                            {/* Action Button - immediately after selection */}
                            <Box display="flex" p={1.5} justifyContent="center">
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled={selectedItems.length === 0}
                                    onClick={() => {
                                        handleItemsSelect(selectedItems);
                                    }}
                                >
                                    {intl.formatMessage({
                                        id: 'sharepoint.select',
                                        defaultMessage: 'Seleziona',
                                    })}
                                </Button>
                            </Box>
                        </>
                    )}

                    {/* Breadcrumbs and Search */}
                    <Grid container spacing={2} flexShrink={0} py={3}>
                        {!searchTerm && (
                            <Grid item xs={12} sm={9}>
                                <BreadcrumbNavigation
                                    breadcrumbs={breadcrumbs}
                                    onBreadcrumbClick={handleBreadcrumbClick}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={searchTerm ? 12 : 3}>
                            <SearchControls searchTerm={searchTerm} onSearchChange={handleSearch} />
                        </Grid>
                    </Grid>

                    {/* File List */}
                    <Box
                        ref={scrollContainerRef}
                        position="relative"
                        flexGrow={1}
                        overflow="auto"
                        height={`calc(100vh - ${isBulkMode ? '710' : '310'}px)`}
                        textAlign="center"
                    >
                        {/* Error Display */}
                        {error && (
                            <Typography color="error" variant="body2">
                                {error}
                            </Typography>
                        )}
                        {!filteredFiles.length ? (
                            <Box flex={1} overflow="auto" height={1} justifyContent="center">
                                {!loading && !error && (
                                    <Typography variant="body2" color="text.secondary">
                                        {searchTerm
                                            ? intl.formatMessage({
                                                  id: 'sharepoint.no-files-match-search',
                                                  defaultMessage:
                                                      'Nessun file corrisponde alla ricerca',
                                              })
                                            : intl.formatMessage({
                                                  id: 'sharepoint.no-files-found',
                                                  defaultMessage: 'Nessun file trovato',
                                              })}
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            <Stack>
                                {fileListItems}

                                {/* Infinite scroll bottom UI for browse mode */}
                                {!searchTerm && (
                                    <InfiniteScrollBottomUI
                                        state={browseInfiniteScrollState.state}
                                        errorMessage={browseInfiniteScrollState.errorMessage}
                                        onRetry={handleLoadMore}
                                        mode="browse"
                                    />
                                )}

                                {/* Infinite scroll bottom UI for search mode */}
                                {searchTerm && (
                                    <InfiniteScrollBottomUI
                                        state={searchInfiniteScrollState.state}
                                        errorMessage={searchInfiniteScrollState.errorMessage}
                                        onRetry={handleLoadMoreSearch}
                                        mode="search"
                                    />
                                )}
                            </Stack>
                        )}
                    </Box>

                    {/* Pagination Controls */}
                    <Stack direction="row" gap={1} alignItems="center" justifyContent="right" p={2}>
                        <Typography variant="body2" color="text.secondary">
                            {!searchTerm && files.length > 0 && (
                                <Stack direction="row" gap={1} alignItems="baseline">
                                    {`${intl.formatMessage({
                                        id: 'sharepoint.showing',
                                        defaultMessage: 'Mostrando',
                                    })} `}
                                    {files.length}
                                    {totalCount
                                        ? `/${totalCount} ${intl.formatMessage({
                                              id: 'sharepoint.items',
                                              defaultMessage: 'elementi',
                                          })}`
                                        : ` ${intl.formatMessage({
                                              id: 'sharepoint.items',
                                              defaultMessage: 'elementi',
                                          })}`}
                                </Stack>
                            )}
                            {searchTerm && searchResults.length > 0 && (
                                <Stack direction="row" gap={1} alignItems="baseline">
                                    {intl.formatMessage({
                                        id: 'sharepoint.found',
                                        defaultMessage: 'Trovati',
                                    })}
                                    {` ${searchResults.length} `}
                                    {intl.formatMessage({
                                        id: 'sharepoint.results-for',
                                        defaultMessage: 'risultati per',
                                    })}
                                    {` "${searchTerm}" `}
                                </Stack>
                            )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({
                                id: 'sharepoint.items-per-page',
                                defaultMessage: 'Elementi per pagina',
                            })}
                        </Typography>
                        <FormControl size="small" variant="standard">
                            <Select
                                value={pageSize}
                                onChange={e => handlePageSizeChange(Number(e.target.value))}
                            >
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
            </Box>
        </Dialog>
    );
};
